import { motion } from "framer-motion";
import { MessageCircle, Mail, Phone, Instagram, Linkedin, Facebook, AtSign, ArrowUpRight } from "lucide-react";
import { MaskLines, Reveal } from "./anim";
import { LeadForm } from "./LeadForm";
import { WHATSAPP, EMAIL, INSTAGRAM, LINKEDIN, FACEBOOK, THREADS, PHONES } from "./data";
import { useBookCall } from "./BookCall";

const SOCIALS = [
  { href: INSTAGRAM, testid: "contact-instagram", Icon: Instagram, label: "Instagram" },
  { href: LINKEDIN, testid: "contact-linkedin", Icon: Linkedin, label: "LinkedIn" },
  { href: FACEBOOK, testid: "contact-facebook", Icon: Facebook, label: "Facebook" },
  { href: THREADS, testid: "contact-threads", Icon: AtSign, label: "Threads" },
];

const ContactChannels = () => (
  <div className="mt-20 grid gap-8 border-t border-[hsl(var(--border))] pt-12 md:grid-cols-3">
    <Reveal>
      <a
        href={`mailto:${EMAIL}`}
        data-testid="contact-email"
        className="group flex items-start gap-4"
      >
        <Mail className="mt-1 text-[hsl(var(--primary))]" size={20} />
        <div>
          <p className="text-xs uppercase tracking-widest text-[hsl(var(--muted))]">Email</p>
          <p className="text-lg transition-colors group-hover:text-[hsl(var(--primary))]">{EMAIL}</p>
        </div>
      </a>
    </Reveal>
    <Reveal delay={0.08}>
      <div className="flex items-start gap-4">
        <Phone className="mt-1 text-[hsl(var(--primary))]" size={20} />
        <div>
          <p className="text-xs uppercase tracking-widest text-[hsl(var(--muted))]">Call / WhatsApp</p>
          {PHONES.map((p) => (
            <a
              key={p}
              href={`tel:${p.replace(/\s/g, "")}`}
              data-testid={`contact-phone-${p.replace(/\D/g, "")}`}
              className="block text-lg transition-colors hover:text-[hsl(var(--primary))]"
            >
              {p}
            </a>
          ))}
        </div>
      </div>
    </Reveal>
    <Reveal delay={0.16}>
      <div className="flex items-start gap-4">
        <span className="mt-1 h-5 w-5 rounded-full bg-[hsl(var(--primary))]" />
        <div>
          <p className="text-xs uppercase tracking-widest text-[hsl(var(--muted))]">Social</p>
          <div className="mt-1 flex flex-wrap gap-3">
            {SOCIALS.map(({ href, testid, Icon, label }) => (
              <a
                key={testid}
                href={href}
                target="_blank"
                rel="noreferrer"
                data-testid={testid}
                aria-label={label}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[hsl(var(--border))] transition-colors hover:bg-[hsl(var(--primary))] hover:text-[hsl(var(--primary-fg))]"
              >
                <Icon size={18} />
              </a>
            ))}
          </div>
        </div>
      </div>
    </Reveal>
  </div>
);

export const Contact = () => {
  const { openBookCall } = useBookCall();
  return (
  <footer
    id="contact"
    data-testid="contact-section"
    className="relative overflow-hidden px-6 pb-32 pt-28 md:px-10 md:pb-14 md:pt-36"
  >
    <motion.div
      aria-hidden
      className="pointer-events-none absolute left-1/2 top-0 h-[500px] w-[700px] -translate-x-1/2 rounded-full blur-[150px]"
      style={{ background: "hsl(var(--glow) / 0.18)" }}
      animate={{ opacity: [0.5, 0.9, 0.5] }}
      transition={{ duration: 8, repeat: Infinity }}
    />

    <div className="relative mx-auto max-w-[1400px]">
      <p className="mb-6 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-[hsl(var(--muted))]">
        <span className="h-px w-8 bg-[hsl(var(--primary))]" /> Let&apos;s talk
      </p>

      <h2 className="font-head text-[15vw] font-bold leading-[0.85] tracking-tight md:text-[11vw] lg:text-[10rem]">
        <MaskLines lines={["Start a", <span key="p" className="italic text-[hsl(var(--primary))]">project.</span>]} />
      </h2>

      <div className="mt-3 grid items-start gap-10 md:mt-4 lg:grid-cols-2 lg:gap-16">
        <div>
          <Reveal delay={0.1}>
            <p className="max-w-xl text-lg text-[hsl(var(--muted))]">
              Book your free 30-minute consultation. Tell us what you&apos;re building — we&apos;ll show you how to make it unforgettable.
            </p>
          </Reveal>

          <Reveal delay={0.18}>
            <div className="mt-10 flex flex-wrap gap-3">
              <a
                href={WHATSAPP}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp-btn"
                className="group flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-8 py-4 text-base font-semibold text-[hsl(var(--primary-fg))] transition-transform duration-300 hover:scale-105"
              >
                <MessageCircle size={18} /> WhatsApp Us
                <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <button
                onClick={openBookCall}
                data-testid="contact-calendly-btn"
                className="rounded-full border border-[hsl(var(--border))] px-8 py-4 text-base font-medium transition-colors duration-300 hover:bg-[hsl(var(--fg)/0.06)]"
              >
                Book a Discovery Call
              </button>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.22}>
          <LeadForm />
        </Reveal>
      </div>

      <ContactChannels />

      <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-[hsl(var(--border))] pt-8 md:flex-row">
        <span className="font-logo text-2xl font-bold italic">
          Design<span className="text-[hsl(var(--primary))]">4</span>you
        </span>
        <p className="text-sm text-[hsl(var(--muted))]">
          © {new Date().getFullYear()} Design4you · Crafted globally since 2015
        </p>
      </div>
    </div>
  </footer>
  );
};
